// src/components/resources/tools/generators/SubtitleOverlay/SubtitleOverlayPreview.tsx
import { safeString } from '~/lib/utils/formatters'
import { getPositionStyle } from './position-styles'

/**
 * Preview component that shows a live preview of the subtitle overlay
 */
export function SubtitleOverlayPreview({ formData }: { formData: Record<string, unknown> }): JSX.Element {
  // Safely extract and convert form data values with proper text trimming
  const rawSubtitleText = safeString(formData.subtitleText) || 'Enter your subtitle text here...'
  // Trim whitespace and normalize line breaks to prevent layout issues
  const subtitleText = rawSubtitleText.trim().replace(/\s+/g, ' ')
  const fontSize = Number(formData.fontSize) || 18
  const fontFamily = safeString(formData.fontFamily) || 'Arial, sans-serif'
  const fontWeight = safeString(formData.fontWeight) || 'normal'
  const lineHeight = Number(formData.lineHeight) || 1.4
  const textColor = safeString(formData.textColor) || '#ffffff'
  const subtitleBackgroundColor = safeString(formData.backgroundColor) || '#000000'
  const backgroundOpacity = Number(formData.backgroundOpacity) || 0.75
  const textShadow = safeString(formData.textShadow) || 'none'
  const borderRadius = Number(formData.borderRadius) || 4
  const textAlignment = safeString(formData.textAlignment) || 'center'
  const maxWidth = formData.maxWidth !== undefined ? Number(formData.maxWidth) : 90
  const position = safeString(formData.position) || 'bottom-center'
  const margin = formData.margin !== undefined ? Number(formData.margin) : 40
  const showPlayerControls = formData.showPlayerControls !== undefined ? Boolean(formData.showPlayerControls) : true
  const backgroundType = safeString(formData.backgroundType) || 'gradient'
  const gradientType = safeString(formData.gradientType) || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  const solidColor = safeString(formData.solidColor) || '#2c3e50'

  // Determine background style
  let backgroundImage = 'none'
  let videoBackgroundColor = '#000000'

  if (backgroundType === 'gradient') {
    backgroundImage = gradientType
  } else if (backgroundType === 'solid') {
    videoBackgroundColor = solidColor
  } else if (backgroundType === 'upload' && formData.backgroundImage) {
    const backgroundImageUrl = safeString(formData.backgroundImage)
    if (backgroundImageUrl) {
      // If the user uploaded a file, it will be a data URL, which works for CSS background-image
      backgroundImage = `url('${backgroundImageUrl}')`
    }
  }

  // Determine position style as an object
  const positionStyle = getPositionStyle(position, margin)

  return (
    <div className="space-y-4">
      {/* Video Mockup - Responsive container with fixed aspect ratio */}
      <div className="w-full max-w-full overflow-hidden">
        <div
          className="relative rounded-lg overflow-hidden shadow-lg w-full"
          style={{
            backgroundColor: videoBackgroundColor,
            backgroundImage,
            backgroundSize: backgroundType === 'upload' ? 'cover' : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '16/9'
          }}
        >
          {/* Subtitle Overlay */}
          <div
            className="absolute"
            style={{
              ...positionStyle,
              maxWidth: `${Math.min(Math.max(maxWidth, 10), 100)}%`,
              width: `${Math.min(Math.max(maxWidth, 10), 100)}%`,
              textAlign: textAlignment as 'left' | 'center' | 'right'
            }}
          >
            <div
              className="inline-block px-4 py-2 rounded break-words"
              style={{
                fontFamily,
                fontSize: `${fontSize}px`,
                fontWeight,
                lineHeight,
                color: textColor,
                backgroundColor: `${subtitleBackgroundColor}${Math.round(backgroundOpacity * 255).toString(16).padStart(2, '0')}`,
                textShadow,
                borderRadius: `${borderRadius}px`,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: textAlignment === 'left' ? 'flex-start' : textAlignment === 'right' ? 'flex-end' : 'center',
                // Ensure proper vertical alignment and prevent extra spacing
                minHeight: 'auto',
                whiteSpace: 'pre-wrap'
              }}
            >
              {subtitleText}
            </div>
          </div>

          {/* Player Controls */}
          {showPlayerControls && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="w-full h-1 bg-white/30 rounded mb-3">
                <div className="w-1/3 h-full bg-red-500 rounded"></div>
              </div>
              <div className="flex items-center justify-between text-white text-sm">
                <div className="flex items-center gap-3">
                  <button className="w-6 h-6 flex items-center justify-center">▶</button>
                  <span>0:35 / 2:14</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span>🔊</span>
                    <div className="w-12 h-1 bg-white/30 rounded">
                      <div className="w-3/4 h-full bg-white rounded"></div>
                    </div>
                  </div>
                  <button className="w-5 h-5 border border-white rounded"></button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Specifications */}
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
          Overlay Specifications
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Text
            </dt>
            <dd className="text-xs text-gray-900 dark:text-gray-100 font-medium truncate">
              {subtitleText}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Font
            </dt>
            <dd className="text-xs text-gray-900 dark:text-gray-100 font-medium">
              {fontFamily} {fontSize}px
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Position
            </dt>
            <dd className="text-xs text-gray-900 dark:text-gray-100 font-medium capitalize">
              {position}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Alignment
            </dt>
            <dd className="text-xs text-gray-900 dark:text-gray-100 font-medium capitalize">
              {textAlignment}
            </dd>
          </div>
        </div>
      </div>
    </div>
  )
}
